/**
 * ImpersonationBar — Bandeau + Modal du "Mode d'incarnation" fondateur.
 *
 * - Bandeau discret visible en HAUT de l'app quand l'incarnation est active.
 * - Modal de sélection accessible depuis ProfileMenu ("Basculer de rôle").
 * - API : GET /api/impersonation/status, /roles ; POST /start, /stop.
 *
 * Affiche uniquement pour un user réel = 'founder'. Sinon totalement invisible.
 */
import React, { useEffect, useState, useCallback } from "react";
import { X, Crown, Loader2, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Compact : bar toujours en position sticky. Modal séparé (utilise Dialog shadcn).

function useImpersonationStatus() {
  const [status, setStatus] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/impersonation/status");
      setStatus(res.data);
    } catch { setStatus(null); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return [status, refresh];
}

// ────────────────────────────────────────────────────────────────────────────
// Bandeau (affiché quand incarnation active)
// ────────────────────────────────────────────────────────────────────────────
export function ImpersonationBanner() {
  const [status, refresh] = useImpersonationStatus();
  const [stopping, setStopping] = useState(false);

  const handleStop = async () => {
    setStopping(true);
    try {
      await api.post("/impersonation/stop", {});
      toast.success("Retour au rôle Fondateur.");
      await refresh();
      // Recharge complète pour rafraîchir tous les menus/permissions
      window.location.reload();
    } catch { toast.error("Impossible de restaurer le rôle."); }
    finally { setStopping(false); }
  };

  if (!status?.is_active) return null;

  return (
    <div
      className="w-full bg-amber-500/95 dark:bg-amber-600/95 text-amber-950 dark:text-amber-50 border-b border-amber-600 shadow-sm"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 text-xs sm:text-sm">
        <Crown className="w-4 h-4 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold uppercase tracking-widest text-[10px] sm:text-xs">
            Mode d'incarnation
          </span>
          <span className="mx-2 opacity-40">·</span>
          <span>
            Rôle actif : <strong>{status.impersonation_role_label || status.impersonation_role}</strong>
          </span>
        </div>
        <button
          onClick={handleStop}
          disabled={stopping}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-950/10 dark:bg-white/10 hover:bg-amber-950/20 dark:hover:bg-white/20 transition-colors disabled:opacity-50 text-[11px] sm:text-xs font-medium"
          data-testid="impersonation-stop-btn"
        >
          {stopping ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
          Revenir à mon rôle Fondateur
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Modal de sélection de rôle (déclenché depuis ProfileMenu)
// ────────────────────────────────────────────────────────────────────────────
export function ImpersonationModal({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [selecting, setSelecting] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [rolesRes, statusRes] = await Promise.all([
          api.get("/impersonation/roles"),
          api.get("/impersonation/status"),
        ]);
        if (!mounted) return;
        setRoles(rolesRes.data?.roles || []);
        setStatus(statusRes.data);
      } catch (e) {
        toast.error("Accès réservé au Fondateur.");
        onOpenChange(false);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [open, onOpenChange]);

  const activate = async (roleKey) => {
    setSelecting(roleKey);
    try {
      await api.post("/impersonation/start", { role: roleKey });
      const r = roles.find((x) => x.key === roleKey);
      toast.success(`Vous naviguez actuellement en tant que : ${r?.label || roleKey}.`);
      onOpenChange(false);
      window.location.reload();
    } catch (e) {
      toast.error("Impossible d'activer ce rôle.");
    } finally { setSelecting(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="impersonation-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-amber-600" /> Basculer de rôle
          </DialogTitle>
          <DialogDescription>
            Émule les permissions d'un autre rôle pour vérifier l'expérience utilisateur.
            Aucune donnée n'est modifiée — tu peux revenir instantanément à ton rôle Fondateur.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
            {roles.filter((r) => r.key !== "founder").map((r) => {
              const isCurrent = status?.impersonation_role === r.key;
              const isBusy = selecting === r.key;
              return (
                <button
                  key={r.key}
                  onClick={() => activate(r.key)}
                  disabled={selecting !== null}
                  className={`
                    text-left p-4 rounded-xl border bg-card transition-all
                    hover:border-primary hover:shadow-md group
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${isCurrent ? "border-amber-500 bg-amber-50 dark:bg-amber-950/20" : "border-border/60"}
                  `}
                  data-testid={`impersonation-role-${r.key}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{r.label}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {r.description}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-primary/60 mt-2">
                        {r.category} · niveau {r.hierarchy_level}
                      </div>
                    </div>
                    {isBusy ? (
                      <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                    )}
                  </div>
                  {isCurrent && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400">
                      Rôle actuellement incarné
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Hook pour savoir si le user réel peut incarner (utile côté ProfileMenu)
// ────────────────────────────────────────────────────────────────────────────
export function useCanImpersonate() {
  const [canImpersonate, setCan] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/impersonation/status");
        setCan(res.data?.is_founder === true);
      } catch { setCan(false); }
    })();
  }, []);
  return canImpersonate;
}
